import PropTypes from 'prop-types';
import { Icon, ButtonTooltip, Popover2 } from '@evertrue/et-components';
import ConfidenceBadge from './shared/confidence-badge';
import { buildUpsellMessage, DEMO_URL, trackUpsellClick } from './shared/upsell-message';
import { formatCurrency } from './utils/index';

const GIVING_SCORE_TOOLTIPS = {
  DSAI: "The DonorSearch AI (DSAI) score uses machine learning to predict a constituent's likelihood to make a charitable gift, based on their giving history, wealth indicators, and philanthropic signals.",
  ECR: "Enhanced CORE Retention is DonorSearch's score that predicts how likely an existing donor is to make a gift within the next twelve months and the following twelve months. This score is generated using national, anonymized data models trained across DonorSearch customers.",
  MLR: "MLR (Most Likely to Respond) is DonorSearch's score that measures a constituent's likelihood to engage and give to your organization within the next 12 months. The score is based on prior giving, email engagement, and event activity to reflect relative donor engagement.",
  DPS: "DPS (Donor Potential Score) is Signal's score that reflects the strength of a constituent's giving pattern. The score is calculated using gift transaction data and is updated nightly for constituents with recorded gift history.",
};

const CATEGORY_TOOLTIPS = {
  charitable: 'Total dollar amount and number of high-confidence gifts made to nonprofit organizations.',
  political: 'Total dollar amount and number of high-confidence gifts made to political organizations.',
  crowdfunding: 'Total dollar amount and number of high-confidence gifts made through crowdfunding platforms.',
};

const DRILL_DOWN_TOOLTIPS = {
  charitable: 'View Charitable Giving matches',
  political: 'View Political Giving matches',
  crowdfunding: 'View Crowdfunding matches',
};

/**
 * Displays giving information including primary score and giving categories with confidence badges.
 */
const GivingCard = ({
  givingScoreType,
  givingScoreValue,
  charitableGiving,
  politicalGiving,
  crowdfunding,
  loading = false,
  hasEnrichmentAccess = false,
  onDrillDown,
  showUpsellTooltip = false,
}) => {
  const hasScore = givingScoreType && givingScoreValue !== null && givingScoreValue !== undefined;

  const handleClick = (category, section = 'highQuality') => {
    if (hasEnrichmentAccess && onDrillDown) {
      onDrillDown(category, section);
    }
  };

  const renderGivingRow = (label, data, category) => {
    const amount = data?.amount;
    const highCount = data?.highCount || 0;
    const lowCount = data?.lowCount || 0;
    const formattedAmount = formatCurrency(amount);
    const isClickable = hasEnrichmentAccess && onDrillDown;
    const isUpsell = showUpsellTooltip && !isClickable;

    const valueContent = (
      <span
        className="giving-card--row-value"
        onClick={isClickable ? () => handleClick(category) : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick(category);
                }
              }
            : undefined
        }
      >
        <span className="giving-card--amount">{formattedAmount || '—'}</span>
        <ConfidenceBadge
          highCount={highCount}
          lowCount={lowCount}
          onHighClick={isClickable ? () => handleClick(category, 'highQuality') : undefined}
          onLowClick={isClickable ? () => handleClick(category, 'lowConfidence') : undefined}
        />
      </span>
    );

    const hasCrowdfundingUpsell = category === 'crowdfunding' && showUpsellTooltip;

    const infoTooltipContent = hasCrowdfundingUpsell ? (
      <span>
        {CATEGORY_TOOLTIPS[category]}
        <br />
        <br />
        Want to unlock full drill-down access and deeper prospect intelligence? Contact your CSM or{' '}
        <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackUpsellClick('crowdfunding')}>
          request a demo
        </a>{' '}
        to see how DonorSearch can power smarter fundraising decisions.
      </span>
    ) : (
      CATEGORY_TOOLTIPS[category]
    );

    const infoIcon = <Icon icon="info-circle" iconKit="gt2" className="giving-card--info-icon" />;

    const hasData = (data?.highCount || 0) > 0 || (data?.lowCount || 0) > 0 || data?.amount;
    const renderedValue =
      isClickable && hasData ? (
        <ButtonTooltip trigger={valueContent} contentWidth={280} disableArrow>
          {DRILL_DOWN_TOOLTIPS[category]}
        </ButtonTooltip>
      ) : (
        valueContent
      );

    return (
      <div className="giving-card--row">
        <div className="giving-card--row-label-wrapper">
          <span className="giving-card--row-label">{label}</span>
          {hasCrowdfundingUpsell ? (
            <Popover2 trigger={infoIcon} contentWidth={280} contentClassName="enrichment-upsell-popover">
              {infoTooltipContent}
            </Popover2>
          ) : (
            <ButtonTooltip trigger={infoIcon} contentWidth={280} disableArrow>
              {infoTooltipContent}
            </ButtonTooltip>
          )}
        </div>
        {loading ? (
          <span className="giving-card--row-value">
            <Icon icon="spinner" spin />
          </span>
        ) : isUpsell ? (
          <Popover2 trigger={valueContent} contentWidth={280} contentClassName="enrichment-upsell-popover">
            {buildUpsellMessage(category, data?.highCount, data?.amount)}
          </Popover2>
        ) : (
          renderedValue
        )}
      </div>
    );
  };

  return (
    <div className="giving-card">
      <div className="giving-card--header">
        <div className="giving-card--header-left">
          <Icon icon="heart" iconKit="gt2" />
          <span className="giving-card--title">Giving</span>
        </div>
        {loading && (
          <div className="giving-card--score">
            <Icon icon="spinner" spin />
          </div>
        )}
        {!loading && hasScore && (
          <div className="giving-card--score">
            <span className="giving-card--score-label">{givingScoreType}</span>
            <span className="giving-card--score-value">{givingScoreValue}/100</span>
            <ButtonTooltip
              trigger={<Icon icon="info-circle" iconKit="gt2" style={{ color: '#B2B2C5' }} />}
              contentWidth={280}
              disableArrow
            >
              {GIVING_SCORE_TOOLTIPS[givingScoreType]}
            </ButtonTooltip>
          </div>
        )}
        {!loading && !hasScore && (
          <div className="giving-card--score">
            <span className="giving-card--score-label">{givingScoreType || 'DPS'}</span>
            <span className="giving-card--score-value giving-card--score-na">N/A</span>
            <ButtonTooltip
              trigger={<Icon icon="info-circle" iconKit="gt2" style={{ color: '#B2B2C5' }} />}
              contentWidth={280}
              disableArrow
            >
              {GIVING_SCORE_TOOLTIPS[givingScoreType] || GIVING_SCORE_TOOLTIPS.DPS}
            </ButtonTooltip>
          </div>
        )}
      </div>

      <div className="giving-card--content">
        {renderGivingRow('Charitable Giving', charitableGiving, 'charitable')}
        {renderGivingRow('Political Giving', politicalGiving, 'political')}
        {renderGivingRow('Crowdfunding', crowdfunding, 'crowdfunding')}
      </div>
    </div>
  );
};

GivingCard.propTypes = {
  givingScoreType: PropTypes.oneOf(['DSAI', 'EC', 'ECR', 'MLR', 'DPS']),
  givingScoreValue: PropTypes.number,
  charitableGiving: PropTypes.shape({
    amount: PropTypes.number,
    highCount: PropTypes.number,
    lowCount: PropTypes.number,
  }),
  politicalGiving: PropTypes.shape({
    amount: PropTypes.number,
    highCount: PropTypes.number,
    lowCount: PropTypes.number,
  }),
  crowdfunding: PropTypes.shape({
    amount: PropTypes.number,
    highCount: PropTypes.number,
    lowCount: PropTypes.number,
  }),
  loading: PropTypes.bool,
  hasEnrichmentAccess: PropTypes.bool,
  onDrillDown: PropTypes.func,
  showUpsellTooltip: PropTypes.bool,
};

export default GivingCard;
